import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static values = {
    allowances: Array,
    workResults: Array,
    allowResults: Array,
    month: String,
    worker: String
  }

  static targets = [
    "thead",
    "tbody",
    "modalBackdrop",
    "modalTitle",
    "detailGrid",
    "allowDetailBody",
    "headerMonth",
    "headerWorker",
    "headerCount"
  ]

  connect() {
    this.weekNames = ['日', '月', '火', '水', '木', '金', '土'];
    this.resultMap = new Set(this.allowResultsValue.map(x => `${x.workId}_${x.allowanceId}`));
    
    // Bind methods to maintain 'this' context when used as event listeners
    this.handleEscape = this.handleEscape.bind(this);
    document.addEventListener('keydown', this.handleEscape);
    
    this.loadData();
  }

  disconnect() {
    document.removeEventListener('keydown', this.handleEscape);
  }

  handleEscape(event) {
    if (event.key === 'Escape') {
      this.closeModal(event);
    }
  }

  hasAllowance(row, columnId) {
    return row.details.some(d => d.column_id === columnId);
  }

  getAllowanceText(row, columnId) {
    const detail = row.details.find(d => d.column_id === columnId);
    if (!detail) return '';
    return `<span class="circle">〇</span>`;
  }

  formatDayWeek(isoDate) {
    const d = new Date(isoDate + 'T00:00:00');
    return `${d.getDate()}日(${this.weekNames[d.getDay()]})`;
  }

  formatMonth(ym) {
    const [y, m] = ym.split('-');
    return `${Number(y)}年${Number(m)}月`;
  }

  setHeader(dataLength) {
    if (this.hasHeaderMonthTarget) {
      this.headerMonthTarget.textContent = `対象年月：${this.formatMonth(this.monthValue)}`;
    }
    if (this.hasHeaderWorkerTarget) {
      this.headerWorkerTarget.textContent = `作業員：${this.workerValue}`;
    }
    if (this.hasHeaderCountTarget) {
      this.headerCountTarget.textContent = `明細：${dataLength}件`;
    }
  }

  buildTable(data) {
    this.theadTarget.innerHTML = '';
    this.tbodyTarget.innerHTML = '';

    let columns = [];
    this.workResultsValue.forEach(row => {
      row.details.forEach(detail => {
        if (detail.category_val < 8) return;

        if (!columns.some(c => c.id === detail.column_id)) {
          columns.push({
            id: detail.column_id,
            name: detail.column_name,
            category: detail.category_val
          });
        }
      });
    });

    columns = columns.filter((col, index, self) => index === self.findIndex((c) => c.id === col.id));
    columns.sort((a, b) => a.category - b.category || a.name.localeCompare(b.name));

    const tr1 = document.createElement('tr');
    tr1.innerHTML = `
      <th class="sticky s1" rowspan="2">日付</th>
      <th class="sticky s2" rowspan="2">事業</th>
      <th class="sticky s3" rowspan="2">作業種</th>
      <th class="sticky s4" rowspan="2">作業時間</th>
      <th class="sticky s5" rowspan="2">工数</th>
      <th class="sticky s6" rowspan="2">利用機械</th>
      <th class="sticky s7" rowspan="2">リース番号</th>
      <th class="sticky s8" rowspan="2">機械時間</th>
      <th class="sticky s9" rowspan="2">機械工数</th>
      <th colspan="${columns.length}">手当実績</th>
    `;
    this.theadTarget.appendChild(tr1);

    const tr2 = document.createElement('tr');
    columns.forEach(col => {
      const th = document.createElement('th');
      th.className = 'allow-col label';
      th.textContent = col.name;
      tr2.appendChild(th);
    });
    this.theadTarget.appendChild(tr2);

    data.forEach(row => {
      const tr = document.createElement('tr');
      tr.className = 'row-link';
      tr.dataset.action = "click->performances--allowance-records-review#openDetail";
      tr.dataset.workId = row.id;

      tr.innerHTML = `
        <td class="sticky s1">${this.formatDayWeek(row.date)}</td>
        <td class="sticky s2">${row.project}</td>
        <td class="sticky s3">${row.workType}</td>
        <td class="sticky s4">${row.workTime}</td>
        <td class="sticky s5">${row.point}</td>
        <td class="sticky s6">${row.machine}</td>
        <td class="sticky s7">${row.leaseNo}</td>
        <td class="sticky s8">${row.machineTime}</td>
        <td class="sticky s9">${row.machinePoint}</td>
      `;

      columns.forEach(col => {
        const td = document.createElement('td');
        td.className = 'allow-col';
        td.innerHTML = this.hasAllowance(row, col.id) ? this.getAllowanceText(row, col.id) : '';
        tr.appendChild(td);
      });
      this.tbodyTarget.appendChild(tr);
    });

    this.setHeader(data.length);
  }

  loadData() {
    const ym = this.monthValue;
    const worker = this.workerValue;
    const data = this.workResultsValue
      .filter(r => r.date.slice(0, 7) === ym && r.worker === worker)
      .sort((a, b) => a.date.localeCompare(b.date) || a.id - b.id);

    this.buildTable(data);
  }

  openDetail(event) {
    const workId = parseInt(event.currentTarget.dataset.workId, 10);
    const row = this.workResultsValue.find(x => x.id === workId);
    if (!row) return;

    const detailItems = [
      ['対象年月', this.formatMonth(row.date.slice(0, 7))],
      ['日付', this.formatDayWeek(row.date)],
      ['作業員', row.worker],
      ['事業', row.project],
      ['作業種', row.workType],
      ['作業時間', row.workTime],
      ['工数', row.point],
      ['利用機械', row.machine],
      ['リース番号', row.leaseNo],
      ['機械時間', row.machineTime],
      ['機械工数', row.machinePoint]
    ];

    this.detailGridTarget.innerHTML = detailItems.map(([k, v]) => `
      <div class="detail-label">${k}</div><div class="detail-value">${v}</div>
    `).join('');

    this.allowDetailBodyTarget.innerHTML = row.details.map(detail => {
      return `
        <tr>
          <td>${detail.category}</td>
          <td>${detail.name || ''}</td>
          <td>${detail.result_text || ''}</td>
          <td class="text-right">${(detail.unitPrice || 0).toLocaleString()}</td>
          <td class="text-right">${(detail.amount || 0).toLocaleString()}</td>
        </tr>
      `;
    }).join('');

    this.modalTitleTarget.textContent = `実績詳細 - ${row.worker} / ${this.formatDayWeek(row.date)} / ${row.project}`;
    this.modalBackdropTarget.classList.add('show');
  }

  closeModal(event = null) {
    if (event) {
      event.preventDefault()
      event.stopPropagation()

      if (event.target !== this.modalBackdropTarget &&
          event.currentTarget === this.modalBackdropTarget) {
        return
      }
    }

    this.modalBackdropTarget.classList.remove("show")
  }

  closeScreen() {
    window.close();
  }
}
